import { generatePath, useHistory, useRouteMatch } from 'react-router-dom';
import { useFragment } from '@apollo/client';
import queryString from 'query-string';

import { checkoutAuthRegistration, swapBuyerCheckout } from '@urls';
import { productDetails } from '@legacyUrls';
import { sendBuyButtonClickEvent } from '@utils/analytics';
import { useBasket } from '@hook/useBasket';
import { useProductSizeSelect } from '@hook/useProductSizeSelect';
import { useFlags } from '@hook/useFlags';
import { useIsAuth } from '@hook/useIsAuth';
import { useAppContext } from '@contexts/AppContext';
import { ProductDetailsCreateOrderButtons } from '@components/ProductDetailsCreateOrderButtons';
import { logger } from '@utils/logger';

import { ProductDetailsCreateOrderButtonsFragment, ProductDetailsCreateOrderButtonsFragmentDoc } from './gql/graphql.v3.generated';
import { ProductDetailsBonusbackFragment, ProductDetailsBonusbackFragmentDoc } from './gql/graphql.v5.generated';

interface RouteParams {
  productId: string,
}

interface Props {
  isInStickyBar?: boolean,
}

export const ProductDetailsCreateOrderButtonsContainer: React.FC<Props> = ({
  isInStickyBar,
}) => {
  const history = useHistory();
  const flags = useFlags();
  const isAuth = useIsAuth();
  const { isOutlet } = useAppContext();

  const match = useRouteMatch<RouteParams>(productDetails);
  const productId = Number(match && match?.params?.productId);
  const { add, isInBasket: getIsInBasket, buyNow } = useBasket();
  const {
    sizes,
    selectedSizeIds,
    loading: isSizesLoading,
    onSelectSize,
  } = useProductSizeSelect(productId);

  const { data: productData, complete: productDataComplete } = useFragment<ProductDetailsCreateOrderButtonsFragment>({
    fragment: ProductDetailsCreateOrderButtonsFragmentDoc,
    from: {
      __typename: 'ProductDetails',
      id: productId,
    },
  });

  const { data: bonusbackData, complete: bonusbackDataComplete } = useFragment<ProductDetailsBonusbackFragment>({
    fragment: ProductDetailsBonusbackFragmentDoc,
    from: {
      __typename: 'ProductType',
      id: productId,
    },
  });

  const handleAddToCart = () => {
    add({ productId, sizeId: selectedSizeIds });
  };
  
  const handleBuyNow = () => {
    sendBuyButtonClickEvent({ referrer: 'product_details' });

    if (!isAuth && !isOutlet && flags.SHAF19621CheckoutAuthorizationEnabled) {
      const sizeId = selectedSizeIds[0] || product && productData?.sizes?.[0]?.id;

      const checkoutAuthPathname = generatePath(checkoutAuthRegistration, {
        productId,
      });

      window.location.href = history.createHref({
        pathname: checkoutAuthPathname,
        search: queryString.stringify({
          ...(sizeId && {
            sizeId,
          }),
        }),
      });
    } else {
      buyNow({ productId, sizeId: selectedSizeIds[0] });
    }
  };

  const handleSwap = () => {
    const sizeId = selectedSizeIds[0] || productData?.sizes?.[0]?.id;

    const swapBuyerCheckoutPathname = generatePath(swapBuyerCheckout, {
      productId,
    });

    const swapChekoutUrl = history.createHref({
      pathname: swapBuyerCheckoutPathname,
      search: queryString.stringify({
        ...(sizeId && {
          sizeId,
        }),
      }),
    });
  
    window.location.href = swapChekoutUrl;
  };

  const product = productData;
  const isCanSwap = product?.canBeExchanged;
  const isInBasket = getIsInBasket({ productId, sizeId: selectedSizeIds });

  if (!productId) {
    logger.error('Invalid route');
    return null;
  }

  return (
    <ProductDetailsCreateOrderButtons
      sizes={sizes}
      selectedSizeIds={selectedSizeIds}
      isInStickyBar={isInStickyBar}
      isInBasket={isInBasket}
      isCanSwap={isCanSwap}
      isLoading={!productDataComplete || isSizesLoading}
      bonusbackInfo={bonusbackDataComplete ? bonusbackData?.bonusbackInfo : undefined}
      onSwap={handleSwap}
      onBuyNow={handleBuyNow}
      onAddToCart={handleAddToCart}
      onSelectSize={onSelectSize}
    />
  );
};
